import React, { useEffect, useState } from "react";
import { Settings, Percent, Landmark, Plus, Trash2, MapPin, Tag } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import useMeta, { refreshMeta } from "@/lib/useMeta";

export default function AdminSettingsPage() {
  const meta = useMeta();
  const [margin, setMargin] = useState(0);
  const [pmethods, setPmethods] = useState([]);
  const [newPM, setNewPM] = useState({ label: "", type: "bank", details: "" });
  const [newCity, setNewCity] = useState("");
  const [newCat, setNewCat] = useState("");

  const load = () => {
    client.get("/admin/settings").then((r) => setMargin(r.data.margin_percent)).catch(() => {});
    client.get("/admin/payment-methods").then((r) => setPmethods(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveMargin = async () => {
    try { await client.put("/admin/settings", { margin_percent: Number(margin) || 0 }); toast.success("تم تحديث نسبة السعر"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addPM = async () => {
    if (!newPM.label.trim()) { toast.error("أدخل اسم طريقة الدفع"); return; }
    try { await client.post("/admin/payment-methods", newPM); toast.success("تمت الإضافة"); setNewPM({ label: "", type: "bank", details: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const togglePM = async (m) => {
    try { await client.put(`/admin/payment-methods/${m.id}`, { ...m, active: !m.active }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delPM = async (id) => {
    try { await client.delete(`/admin/payment-methods/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addCity = async () => {
    if (!newCity.trim()) return toast.error("أدخل اسم المدينة");
    try { await client.post("/admin/cities", { name: newCity.trim() }); await refreshMeta(); setNewCity(""); toast.success("تمت إضافة المدينة"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delCity = async (name) => {
    try { await client.delete(`/admin/cities/${encodeURIComponent(name)}`); await refreshMeta(); toast.success("تم حذف المدينة"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addCat = async () => {
    if (!newCat.trim()) return toast.error("أدخل اسم التصنيف");
    try { await client.post("/admin/categories", { name: newCat.trim() }); await refreshMeta(); setNewCat(""); toast.success("تمت إضافة التصنيف"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delCat = async (id) => {
    try { await client.delete(`/admin/categories/${id}`); await refreshMeta(); toast.success("تم حذف التصنيف"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div dir="rtl">
      <h2 className="font-head text-[16px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><Settings size={17} style={{ color: "var(--k-gold-deep)" }} /> الإعدادات</h2>

      <h3 className="font-head text-[14px] font-bold px-4 pt-3 pb-2 flex items-center gap-1.5"><Percent size={15} style={{ color: "var(--k-gold-deep)" }} /> نسبة الربح على الأسعار</h3>
      <div className="rounded-2xl mx-4 p-4 mb-1" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <p className="text-[11.5px] mb-2 leading-relaxed" style={{ color: "var(--k-ink-soft)" }}>تُضاف هذه النسبة على كل الأسعار التي يراها المشترون (0 = بدون).</p>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center rounded-lg px-3" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
            <input data-testid="margin-input" type="number" step="0.5" value={margin} onChange={(e) => setMargin(e.target.value)} className="w-full bg-transparent py-2.5 text-[14px] font-num font-bold outline-none" />
            <span className="text-[13px]" style={{ color: "var(--k-ink-soft)" }}>%</span>
          </div>
          <button data-testid="margin-save" onClick={saveMargin} className="k-tap px-5 rounded-lg text-white font-bold text-[13px]" style={{ background: "var(--k-ink)" }}>حفظ</button>
        </div>
      </div>

      <h3 className="font-head text-[14px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><Landmark size={15} style={{ color: "var(--k-gold-deep)" }} /> طرق الدفع ({pmethods.length})</h3>
      <div className="rounded-2xl mx-4 p-4 mb-1" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <input data-testid="pm-label" value={newPM.label} onChange={(e) => setNewPM((p) => ({ ...p, label: e.target.value }))} placeholder="اسم الطريقة (مثال: مصرف الجمهورية — تحويل بنكي)" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
        <textarea data-testid="pm-details" value={newPM.details} onChange={(e) => setNewPM((p) => ({ ...p, details: e.target.value }))} placeholder="التفاصيل: رقم الحساب / IBAN / تعليمات التحويل" rows={2} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
        <button data-testid="pm-add" onClick={addPM} className="k-tap w-full py-2.5 rounded-lg text-white font-bold text-[13px] flex items-center justify-center gap-1" style={{ background: "var(--k-ink)" }}><Plus size={15} /> إضافة طريقة دفع</button>
      </div>
      {pmethods.map((m) => (
        <div key={m.id} data-testid={`pm-${m.id}`} className="rounded-2xl mx-4 p-3.5 mb-2" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <p className="font-head text-[13px] font-bold">{m.label}</p>
              {m.details && <p className="text-[11px] whitespace-pre-line mt-0.5" style={{ color: "var(--k-ink-soft)" }}>{m.details}</p>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button data-testid={`pm-toggle-${m.id}`} onClick={() => togglePM(m)} className="k-tap text-[11px] font-bold px-2.5 py-1.5 rounded-lg" style={{ background: m.active ? "var(--k-emerald-tint)" : "var(--k-paper)", color: m.active ? "var(--k-emerald)" : "var(--k-ink-soft)", border: "1px solid var(--k-line)" }}>{m.active ? "مُفعّلة" : "متوقفة"}</button>
              <button data-testid={`pm-del-${m.id}`} onClick={() => delPM(m.id)} className="k-tap px-2.5 py-1.5 rounded-lg" style={{ background: "var(--k-ruby-tint)", color: "var(--k-ruby)" }}><Trash2 size={13} /></button>
            </div>
          </div>
        </div>
      ))}

      <h3 className="font-head text-[14px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><MapPin size={15} style={{ color: "var(--k-gold-deep)" }} /> المدن ({meta.cities.length})</h3>
      <div className="rounded-2xl mx-4 p-4 mb-1" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <div className="flex gap-2 mb-3">
          <input data-testid="new-city-input" value={newCity} onChange={(e) => setNewCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCity()} placeholder="اسم مدينة جديدة" className="flex-1 rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
          <button data-testid="add-city-btn" onClick={addCity} className="k-tap px-4 rounded-lg text-white font-bold text-[13px] flex items-center gap-1" style={{ background: "var(--k-ink)" }}><Plus size={16} /> إضافة</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {meta.cities.map((c) => (
            <span key={c} data-testid={`city-tag-${c}`} className="flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-full" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
              {c}<button data-testid={`del-city-${c}`} onClick={() => delCity(c)} className="k-tap" style={{ color: "var(--k-ruby)" }}><Trash2 size={13} /></button>
            </span>
          ))}
        </div>
      </div>

      <h3 className="font-head text-[14px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5"><Tag size={15} style={{ color: "var(--k-gold-deep)" }} /> التصنيفات ({meta.categories.length})</h3>
      <div className="rounded-2xl mx-4 p-4 mb-6" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <div className="flex gap-2 mb-3">
          <input data-testid="new-cat-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="اسم تصنيف جديد" className="flex-1 rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
          <button data-testid="add-cat-btn" onClick={addCat} className="k-tap px-4 rounded-lg text-white font-bold text-[13px] flex items-center gap-1" style={{ background: "var(--k-ink)" }}><Plus size={16} /> إضافة</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {meta.categories.map((c) => (
            <span key={c.id} data-testid={`cat-tag-${c.id}`} className="flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-full" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
              {c.name}<button data-testid={`del-cat-${c.id}`} onClick={() => delCat(c.id)} className="k-tap" style={{ color: "var(--k-ruby)" }}><Trash2 size={13} /></button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
